import React from 'react';
import { Grid, Card, Typography, Row, Col, Badge } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';
import { formatDate } from '../../utils/dateFormatters';
import { filterByNits } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

/**
 * ImprovementMomentumChart - Tendencia de Mejora del Inventario
 *
 * Compara el inicio vs fin del periodo para saber si el inventario mejoro o empeoro.
 * Si el inventario saludable crecio mas de lo que crecio el critico, estas mejorando.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 */
function ImprovementMomentumChart({ data, nitList }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;

  // Calculate improvement metrics
  const metrics = React.useMemo(() => {
    if (!data || data.length === 0) return null;

    const relevantData = filterByNits(data, nitList);

    // Aggregate by date if grouped by NIT
    const groupedByDate = {};
    relevantData.forEach(record => {
      const date = record.RecordDate;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          RecordDate: date,
          VerdeCount: 0,
          AzulCount: 0,
          NegroCount: 0,
          RojoCount: 0,
          AmarilloCount: 0,
          TotalReferences: 0
        };
      }
      groupedByDate[date].VerdeCount += record.VerdeCount || 0;
      groupedByDate[date].AzulCount += record.AzulCount || 0;
      groupedByDate[date].NegroCount += record.NegroCount || 0;
      groupedByDate[date].RojoCount += record.RojoCount || 0;
      groupedByDate[date].AmarilloCount += record.AmarilloCount || 0;
      groupedByDate[date].TotalReferences += record.TotalReferences || 0;
    });

    const sortedData = Object.values(groupedByDate)
      .sort((a, b) => a.RecordDate.localeCompare(b.RecordDate));

    if (sortedData.length < 2) return null;

    const first = sortedData[0];
    const last = sortedData[sortedData.length - 1];

    // Categorías
    const firstHealthy = first.VerdeCount + first.AmarilloCount;
    const lastHealthy = last.VerdeCount + last.AmarilloCount;
    const firstWarning = first.AzulCount;
    const lastWarning = last.AzulCount;
    const firstCritical = first.NegroCount + first.RojoCount;
    const lastCritical = last.NegroCount + last.RojoCount;

    // Porcentajes
    const firstHealthyPercent = first.TotalReferences > 0
      ? (firstHealthy / first.TotalReferences) * 100
      : 0;
    const lastHealthyPercent = last.TotalReferences > 0
      ? (lastHealthy / last.TotalReferences) * 100
      : 0;
    const firstWarningPercent = first.TotalReferences > 0
      ? (firstWarning / first.TotalReferences) * 100
      : 0;
    const lastWarningPercent = last.TotalReferences > 0
      ? (lastWarning / last.TotalReferences) * 100
      : 0;
    const firstCriticalPercent = first.TotalReferences > 0
      ? (firstCritical / first.TotalReferences) * 100
      : 0;
    const lastCriticalPercent = last.TotalReferences > 0
      ? (lastCritical / last.TotalReferences) * 100
      : 0;

    // Cambios absolutos y relativos
    const healthyChange = lastHealthy - firstHealthy;
    const healthyPercentChange = lastHealthyPercent - firstHealthyPercent;
    const warningChange = lastWarning - firstWarning;
    const criticalChange = lastCritical - firstCritical;
    const criticalPercentChange = lastCriticalPercent - firstCriticalPercent;

    // Score de momentum (-100 a 100)
    // Positivo cuando mejora saludable, negativo cuando empeora
    const momentumScore = healthyPercentChange - criticalPercentChange;

    // Calculate trend data for the chart
    const trendData = sortedData.map(item => {
      const healthy = item.VerdeCount + item.AmarilloCount;
      const critical = item.NegroCount + item.RojoCount;
      return {
        RecordDate: item.RecordDate,
        healthy: item.TotalReferences > 0 ? (healthy / item.TotalReferences) * 100 : 0,
        critical: item.TotalReferences > 0 ? (critical / item.TotalReferences) * 100 : 0
      };
    });

    // Determine momentum status
    let momentumStatus = 'neutral';
    if (momentumScore > 10) {
      momentumStatus = 'strong-positive';
    } else if (momentumScore > 3) {
      momentumStatus = 'positive';
    } else if (momentumScore < -10) {
      momentumStatus = 'strong-negative';
    } else if (momentumScore < -3) {
      momentumStatus = 'negative';
    }

    return {
      firstDate: first.RecordDate,
      lastDate: last.RecordDate,
      firstHealthy,
      lastHealthy,
      firstHealthyPercent,
      lastHealthyPercent,
      firstWarning,
      lastWarning,
      firstWarningPercent,
      lastWarningPercent,
      firstCritical,
      lastCritical,
      firstCriticalPercent,
      lastCriticalPercent,
      healthyChange,
      healthyPercentChange,
      warningChange,
      criticalChange,
      criticalPercentChange,
      momentumScore,
      momentumStatus,
      totalSKUs: last.TotalReferences,
      trendData
    };
  }, [data, nitList]);

  // Get momentum configuration
  const getMomentumConfig = (status, score) => {
    const configs = {
      'strong-positive': {
        label: 'Mejora Fuerte',
        shortLabel: 'Fuerte Mejora',
        color: '#16a34a',
        bgColor: '#dcfce7',
        borderColor: '#86efac',
        icon: <RiseOutlined />,
        description: 'Mejora significativa en la salud del inventario',
        subtext: `+${score.toFixed(1)} puntos de momentum`
      },
      'positive': {
        label: 'Mejora Moderada',
        shortLabel: 'Mejora',
        color: '#4ade80',
        bgColor: '#dcfce7',
        borderColor: '#86efac',
        icon: <ArrowUpOutlined />,
        description: 'Tendencia positiva en el inventario',
        subtext: `+${score.toFixed(1)} puntos de momentum`
      },
      'neutral': {
        label: 'Estable',
        shortLabel: 'Estable',
        color: '#6b7280',
        bgColor: '#f3f4f6',
        borderColor: '#d1d5db',
        icon: '→',
        description: 'Sin cambios significativos en el periodo',
        subtext: `${score.toFixed(1)} puntos de momentum`
      },
      'negative': {
        label: 'Deterioro Moderado',
        shortLabel: 'Deterioro',
        color: '#f97316',
        bgColor: '#ffedd5',
        borderColor: '#fdba74',
        icon: <ArrowDownOutlined />,
        description: 'Tendencia negativa en el inventario',
        subtext: `${score.toFixed(1)} puntos de momentum`
      },
      'strong-negative': {
        label: 'Deterioro Fuerte',
        shortLabel: 'Fuerte Deterioro',
        color: '#dc2626',
        bgColor: '#fee2e2',
        borderColor: '#fca5a5',
        icon: <FallOutlined />,
        description: 'Deterioro significativo en la salud del inventario',
        subtext: `${score.toFixed(1)} puntos de momentum`
      }
    };
    return configs[status] || configs.neutral;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '160px'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
          {formatDate(label)}
        </p>
        {payload.map((entry, index) => (
          <p key={index} style={{
            margin: '4px 0',
            color: entry.name === 'Saludable' ? '#16a34a' : '#dc2626',
            fontWeight: 500
          }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color, marginRight: 6 }}></span>
            {entry.name}: {entry.value.toFixed(1)}%
          </p>
        ))}
      </div>
    );
  };

  if (!metrics) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{
          height: '100%',
          borderRadius: '12px',
          backgroundColor: '#fafafa'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: isXs ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Datos insuficientes
          </div>
          <div style={{ fontSize: isXs ? 12 : 13 }}>
            Se requieren al menos 2 días de datos para calcular el momentum
          </div>
        </div>
      </Card>
    );
  }

  const momentumConfig = getMomentumConfig(metrics.momentumStatus, metrics.momentumScore);
  const chartHeight = isXs ? 180 : 240;

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
      }}
      bodyStyle={{ padding: isXs ? 16 : 20 }}
    >
      {/* Header with Momentum Status */}
      <div style={{ marginBottom: isXs ? 16 : 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Text strong style={{ fontSize: isXs ? 14 : 16, color: '#1f2937' }}>
            Tendencia de Mejora del Inventario
          </Text>
          <Badge
            count={momentumConfig.shortLabel}
            style={{
              backgroundColor: momentumConfig.bgColor,
              color: momentumConfig.color,
              border: `1px solid ${momentumConfig.borderColor}`,
              fontSize: isXs ? 11 : 12,
              fontWeight: 600,
              padding: '4px 12px'
            }}
          />
        </div>
        <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
          Periodo: {formatDate(metrics.firstDate)} → {formatDate(metrics.lastDate)}
        </Text>
      </div>

      {/* Momentum Status Banner */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: momentumConfig.bgColor,
        borderRadius: '10px',
        borderLeft: `4px solid ${momentumConfig.color}`,
        marginBottom: isXs ? 16 : 20,
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <span style={{ fontSize: 24, color: momentumConfig.color }}>
          {typeof momentumConfig.icon === 'string' ? momentumConfig.icon : null}
        </span>
        <span style={{ fontSize: 18, color: momentumConfig.color }}>
          {typeof momentumConfig.icon !== 'string' ? momentumConfig.icon : null}
        </span>
        <div>
          <Text strong style={{ color: momentumConfig.color, fontSize: isXs ? 13 : 14 }}>
            {momentumConfig.label}
          </Text>
          <Paragraph style={{
            margin: '2px 0 0 0',
            fontSize: isXs ? 11 : 12,
            color: '#6b7280'
          }}>
            {momentumConfig.description}
          </Paragraph>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: isXs ? 16 : 20 }}>
        <Col xs={12} sm={8}>
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: isXs ? 11 : 12, color: '#6b7280', marginBottom: 4 }}>
              SKUs Saludables
            </div>
            <div style={{
              fontSize: isXs ? 18 : 22,
              fontWeight: 700,
              color: metrics.healthyChange >= 0 ? '#16a34a' : '#dc2626',
              lineHeight: 1
            }}>
              {metrics.healthyChange > 0 ? '+' : ''}{metrics.healthyChange}
            </div>
            <div style={{ fontSize: isXs ? 10 : 11, color: '#9ca3af' }}>
              {metrics.firstHealthy} → {metrics.lastHealthy} SKUs
            </div>
          </div>
        </Col>
        <Col xs={12} sm={8}>
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: isXs ? 11 : 12, color: '#6b7280', marginBottom: 4 }}>
              Cambio Saludable
            </div>
            <div style={{
              fontSize: isXs ? 18 : 22,
              fontWeight: 700,
              color: metrics.healthyPercentChange >= 0 ? '#16a34a' : '#dc2626',
              lineHeight: 1
            }}>
              {metrics.healthyPercentChange > 0 ? '+' : ''}{metrics.healthyPercentChange.toFixed(1)}%
            </div>
            <div style={{ fontSize: isXs ? 10 : 11, color: '#9ca3af' }}>
              {metrics.firstHealthyPercent.toFixed(1)}% → {metrics.lastHealthyPercent.toFixed(1)}%
            </div>
          </div>
        </Col>
        <Col xs={12} sm={8}>
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: isXs ? 11 : 12, color: '#6b7280', marginBottom: 4 }}>
              SKUs Críticos
            </div>
            <div style={{
              fontSize: isXs ? 18 : 22,
              fontWeight: 700,
              color: metrics.criticalChange <= 0 ? '#16a34a' : '#dc2626',
              lineHeight: 1
            }}>
              {metrics.criticalChange > 0 ? '+' : ''}{metrics.criticalChange}
            </div>
            <div style={{ fontSize: isXs ? 10 : 11, color: '#9ca3af' }}>
              {metrics.firstCritical} → {metrics.lastCritical} SKUs
            </div>
          </div>
        </Col>
      </Row>

      {/* Chart */}
      <div style={{ marginBottom: isXs ? 16 : 20 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart
            data={metrics.trendData}
            margin={{ top: 20, right: 20, left: 40, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="RecordDate"
              tickFormatter={formatDate}
              tick={{ fontSize: isXs ? 9 : 10 }}
              interval={isXs ? 1 : 0}
              angle={isXs ? -45 : 0}
              textAnchor="end"
              height={isXs ? 40 : 60}
            />
            <YAxis
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: isXs ? 9 : 10 }}
              label={{ value: '% de SKUs', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={50} stroke="#9ca3af" strokeDasharray="3 3" label="50%" strokeOpacity={0.5} />
            <Line
              type="monotone"
              dataKey="healthy"
              name="Saludable"
              stroke="#16a34a"
              strokeWidth={2}
              dot={{ r: isXs ? 3 : 4 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="critical"
              name={"Crítico"}
              stroke="#dc2626"
              strokeWidth={2}
              dot={{ r: isXs ? 3 : 4 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Methodology Legend */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: isXs ? 14 : 16,
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: isXs ? 12 : 13, color: '#1f2937' }}>
            📐 Metodología del Cálculo
          </Text>
        </div>

        <Row gutter={[16, 10]}>
          <Col xs={12} sm={8}>
            <div style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: isXs ? 10 : 11, fontWeight: 600, color: '#374151' }}>
                1. Definición de Categorías
              </Text>
              <Paragraph style={{
                margin: '2px 0 0 0',
                fontSize: isXs ? 9 : 10,
                color: '#6b7280',
                marginBottom: 0
              }}>
              Saludable: Verde+Amarillo | Sobre-inventario: Azul | Crítico: Negro+Rojo
            </Paragraph>
            </div>
          </Col>

          <Col xs={12} sm={8}>
            <div style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: isXs ? 10 : 11, fontWeight: 600, color: '#374151' }}>
                2. Porcentaje por Categoría
              </Text>
              <Paragraph style={{
                margin: '2px 0 0 0',
                fontSize: isXs ? 9 : 10,
                color: '#6b7280',
                fontFamily: 'monospace',
                marginBottom: 0
              }}>
              % = (Conteo / Total) × 100
              </Paragraph>
            </div>
          </Col>

          <Col xs={12} sm={8}>
            <div style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: isXs ? 10 : 11, fontWeight: 600, color: '#374151' }}>
                3. Comparación de Períodos
              </Text>
              <Paragraph style={{
                margin: '2px 0 0 0',
                fontSize: isXs ? 9 : 10,
                color: '#6b7280',
                marginBottom: 0
              }}>
              Cambio = Final - Inicial (absoluto y %)
              </Paragraph>
            </div>
          </Col>

          <Col xs={12} sm={8}>
            <div style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: isXs ? 10 : 11, fontWeight: 600, color: '#374151' }}>
                4. Score de Momentum
              </Text>
              <Paragraph style={{
                margin: '2px 0 0 0',
                fontSize: isXs ? 9 : 10,
                color: '#6b7280',
                fontFamily: 'monospace',
                marginBottom: 0
              }}>
              Score = Δ%Saludable - Δ%Crítico
              </Paragraph>
            </div>
          </Col>
        </Row>

        {/* Score Detail */}
        <div style={{
          marginTop: 10,
          padding: '8px 12px',
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          border: '1px dashed #d1d5db',
          fontSize: isXs ? 9 : 10,
          color: '#4b5563',
          fontFamily: 'monospace'
        }}>
          <div>
            <Text strong style={{ fontSize: isXs ? 9 : 10 }}>Score: </Text>
            {metrics.healthyPercentChange.toFixed(1)}% (Saludable) - ({metrics.criticalPercentChange > 0 ? '+' : ''}{metrics.criticalPercentChange.toFixed(1)}% (Crítico)) = <Text strong style={{ color: momentumConfig.color }}>{metrics.momentumScore.toFixed(1)} pts</Text>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default ImprovementMomentumChart;
